import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { History, Search, Download, Copy, Check } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

type Row = {
  id: string;
  rotated_at: string;
  request_id: string | null;
  target_user_id: string;
  target_email: string | null;
  target_name: string | null;
  target_roles: string[];
  actor_id: string | null;
  actor_role: string | null;
  actor_name: string | null;
  actor_email: string | null;
};

const pretty = (r: string) => r.replace(/_/g, ' ');

export default function PasswordRotationAuditLog() {
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pw-rotation-audit'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('staff_password_rotations' as any)
        .select('id, rotated_at, request_id, target_user_id, target_email, target_roles, actor_id, actor_role')
        .order('rotated_at', { ascending: false })
        .limit(500);
      if (error) throw error;

      const ids = new Set<string>();
      (rows || []).forEach((r: any) => {
        if (r.target_user_id) ids.add(r.target_user_id);
        if (r.actor_id) ids.add(r.actor_id);
      });
      const map = new Map<string, { full_name: string | null; email: string | null }>();
      if (ids.size) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('user_id, full_name, email')
          .in('user_id', [...ids]);
        (profs || []).forEach((p: any) =>
          map.set(p.user_id, { full_name: p.full_name || null, email: p.email || null }),
        );
      }
      return (rows as any[]).map((r) => {
        const t = map.get(r.target_user_id) || { full_name: null, email: null };
        const a = r.actor_id ? map.get(r.actor_id) : null;
        return {
          id: r.id,
          rotated_at: r.rotated_at,
          request_id: r.request_id || null,
          target_user_id: r.target_user_id,
          target_email: r.target_email || t.email,
          target_name: t.full_name,
          target_roles: r.target_roles || [],
          actor_id: r.actor_id,
          actor_role: r.actor_role,
          actor_name: a?.full_name || null,
          actor_email: a?.email || null,
        } as Row;
      });
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data || [];
    return (data || []).filter((r) =>
      [
        r.target_name, r.target_email, r.actor_name, r.actor_email,
        r.actor_role, r.request_id, ...r.target_roles,
      ].some((v) => (v || '').toString().toLowerCase().includes(q)),
    );
  }, [data, search]);

  const copyId = async (id: string) => {
    await navigator.clipboard.writeText(id);
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 1200);
  };

  const exportCsv = () => {
    const head = ['rotated_at', 'request_id', 'target_user_id', 'target_email', 'target_name',
      'target_roles', 'actor_id', 'actor_email', 'actor_name', 'actor_role'];
    const escape = (v: any) => {
      const s = (v ?? '').toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [head.join(',')]
      .concat(filtered.map((r) => [
        r.rotated_at, r.request_id || '', r.target_user_id, r.target_email || '',
        r.target_name || '', (r.target_roles || []).join('|'), r.actor_id || '',
        r.actor_email || '', r.actor_name || '', r.actor_role || '',
      ].map(escape).join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `password-rotations-${format(new Date(), 'yyyyMMdd-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported CSV');
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <History className="h-6 w-6 text-primary" /> Password Rotation Audit Log
        </h1>
        <p className="text-sm text-muted-foreground">
          Every staff password rotation, with actor, target, roles, timestamp, and function request ID.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">
            {filtered.length} event{filtered.length === 1 ? '' : 's'}
          </CardTitle>
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search target, actor, role, request ID"
                className="pl-7 h-8 text-xs w-[280px]"
              />
            </div>
            <Button size="sm" variant="outline" className="h-8 gap-1" onClick={exportCsv} disabled={!filtered.length}>
              <Download className="h-3.5 w-3.5" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-64 m-4" />
          ) : !filtered.length ? (
            <p className="text-sm text-muted-foreground text-center py-12">No rotation events found.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Request ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        <div>{format(new Date(r.rotated_at), 'd MMM yyyy, HH:mm')}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(r.rotated_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{r.actor_name || r.actor_email || '—'}</div>
                        {r.actor_role && (
                          <div className="text-[10px] text-muted-foreground capitalize">{pretty(r.actor_role)}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{r.target_name || '—'}</div>
                        <div className="text-[10px] text-muted-foreground">{r.target_email || '—'}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {(r.target_roles || []).map((role) => (
                            <Badge key={role} variant="outline" className="text-[10px] capitalize">
                              {pretty(role)}
                            </Badge>
                          ))}
                          {!r.target_roles?.length && <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-[11px] font-mono">
                        {r.request_id ? (
                          <button
                            onClick={() => copyId(r.request_id!)}
                            className="inline-flex items-center gap-1 hover:text-primary"
                            title="Copy request ID"
                          >
                            <span className="truncate max-w-[180px]">{r.request_id}</span>
                            {copied === r.request_id ? (
                              <Check className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <Copy className="h-3 w-3 opacity-60" />
                            )}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
